import { useCallback, useMemo, useState } from 'react';

import type { ConfigurationFile, EnvironmentSummary, ServiceDefinition } from '@/api/configuration';
import {
    createConfigFile,
    deleteConfigFile,
    updateConfigFile,
} from '@/api/configuration';
import { triggerConfigDeploy } from '@/api/deploy';
import { ConfigDeployStatusBadge } from '@/components/deploy/config-deploy-status-badge';
import { ConfigFileListItem } from '@/components/deploy/config-file-list-item';
import type { ConfigFileValidationErrors } from '@/components/deploy/config-file-validation';
import { isFormValid, validateConfigFileForm } from '@/components/deploy/config-file-validation';
import { EnvironmentOverridePanel } from '@/components/deploy/environment-override-panel';
import { resolveVersionPath } from '@/components/deploy/version-path';
import { VersionSelector } from '@/components/deploy/version-selector';
import { useConfigDeployStatus } from '@/hooks/use-config-deploy-status';
import { useConfigFiles, useEnvironments, useInvalidateConfiguration, useServiceDefinitions } from '@/hooks/use-configuration';

// --- Shared UI helpers ---

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-content-tertiary">{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// --- Config File Form ---

function ConfigFileForm({
  initial,
  serviceDefinitions,
  onSave,
  onCancel,
}: {
  initial?: ConfigurationFile;
  serviceDefinitions: ServiceDefinition[];
  onSave: (data: Omit<ConfigurationFile, 'id' | 'projectKey'>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [defaultSourceRoot, setDefaultSourceRoot] = useState(initial?.defaultSourceRoot ?? '1_Shared');
  const [sourceLocation, setSourceLocation] = useState(initial?.sourceLocation ?? '');
  const [sourceFilename, setSourceFilename] = useState(initial?.sourceFilename ?? '');
  const [targetLocation, setTargetLocation] = useState(initial?.targetLocation ?? '');
  const [targetFilename, setTargetFilename] = useState(initial?.targetFilename ?? '');
  const [ownerGroup, setOwnerGroup] = useState(initial?.ownerGroup ?? '');
  const [ownerName, setOwnerName] = useState(initial?.ownerName ?? '');
  const [type, setType] = useState(initial?.type ?? '');
  const [serviceDefinitionId, setServiceDefinitionId] = useState(initial?.serviceDefinitionId ?? '');
  const [errors, setErrors] = useState<ConfigFileValidationErrors>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateConfigFileForm({
      name,
      defaultSourceRoot,
      sourceLocation,
      sourceFilename,
      targetLocation,
      targetFilename,
      ownerGroup,
      ownerName,
      type,
      serviceDefinitionId,
    });
    setErrors(validationErrors);
    if (!isFormValid(validationErrors)) return;
    onSave({
      name,
      defaultSourceRoot: defaultSourceRoot || undefined,
      sourceLocation: sourceLocation || undefined,
      sourceFilename: sourceFilename || undefined,
      targetLocation: targetLocation || undefined,
      targetFilename: targetFilename || undefined,
      ownerGroup: ownerGroup || undefined,
      ownerName: ownerName || undefined,
      type: type || undefined,
      serviceDefinitionId: serviceDefinitionId || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-3">
      <p className="text-xs font-semibold text-content-secondary">
        {initial ? 'Edit Configuration File' : 'New Configuration File'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="sm:col-span-2 lg:col-span-3">
          <label htmlFor="cf-name" className="block text-xs font-medium text-content-secondary mb-1">Name *</label>
          <input id="cf-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="application.yml" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>}
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label htmlFor="cf-default-source-root" className="block text-xs font-medium text-content-secondary mb-1">Default Source Root</label>
          <input id="cf-default-source-root" type="text" value={defaultSourceRoot} onChange={(e) => setDefaultSourceRoot(e.target.value)} placeholder="1_Shared" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.defaultSourceRoot && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.defaultSourceRoot}</p>}
        </div>
        <div>
          <label htmlFor="cf-source-location" className="block text-xs font-medium text-content-secondary mb-1">Source Location</label>
          <input id="cf-source-location" type="text" value={sourceLocation} onChange={(e) => setSourceLocation(e.target.value)} placeholder="/repo/config/templates" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.sourceLocation && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.sourceLocation}</p>}
        </div>
        <div>
          <label htmlFor="cf-source-filename" className="block text-xs font-medium text-content-secondary mb-1">Source Filename</label>
          <input id="cf-source-filename" type="text" value={sourceFilename} onChange={(e) => setSourceFilename(e.target.value)} placeholder="application.yml.j2" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.sourceFilename && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.sourceFilename}</p>}
        </div>
        <div>
          <label htmlFor="cf-target-location" className="block text-xs font-medium text-content-secondary mb-1">Target Location</label>
          <input id="cf-target-location" type="text" value={targetLocation} onChange={(e) => setTargetLocation(e.target.value)} placeholder="/opt/apps/my-app/config" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.targetLocation && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.targetLocation}</p>}
        </div>
        <div>
          <label htmlFor="cf-target-filename" className="block text-xs font-medium text-content-secondary mb-1">Target Filename</label>
          <input id="cf-target-filename" type="text" value={targetFilename} onChange={(e) => setTargetFilename(e.target.value)} placeholder="application.yml" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.targetFilename && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.targetFilename}</p>}
        </div>
        <div>
          <label htmlFor="cf-owner-group" className="block text-xs font-medium text-content-secondary mb-1">Owner Group</label>
          <input id="cf-owner-group" type="text" value={ownerGroup} onChange={(e) => setOwnerGroup(e.target.value)} placeholder="appuser" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.ownerGroup && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.ownerGroup}</p>}
        </div>
        <div>
          <label htmlFor="cf-owner-name" className="block text-xs font-medium text-content-secondary mb-1">Owner Name</label>
          <input id="cf-owner-name" type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="appuser" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.ownerName && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.ownerName}</p>}
        </div>
        <div>
          <label htmlFor="cf-type" className="block text-xs font-medium text-content-secondary mb-1">Type</label>
          <input id="cf-type" type="text" value={type} onChange={(e) => setType(e.target.value)} placeholder="template, static, properties..." className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
          {errors.type && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.type}</p>}
        </div>
        <div>
          <label htmlFor="cf-service-def" className="block text-xs font-medium text-content-secondary mb-1">Service Definition</label>
          <select id="cf-service-def" value={serviceDefinitionId} onChange={(e) => setServiceDefinitionId(e.target.value)} className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600">
            <option value="">--None--</option>
            {serviceDefinitions.map((def) => (
              <option key={def.id} value={def.id}>{def.name}{def.type ? ` (${def.type})` : ''}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          {initial ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Deploy Files Modal ---

function DeployFilesModal({
  files,
  environments,
  projectKey,
  defaultEnvironmentId,
  onClose,
  onDeploy,
}: {
  files: ConfigurationFile[];
  environments: EnvironmentSummary[];
  projectKey: string;
  defaultEnvironmentId?: string;
  onClose: () => void;
  onDeploy: (environmentId: string, fileIds: string[], versionPath: string, version: string) => Promise<void>;
}) {
  const sortedFiles = useMemo(
    () => [...files].sort((a, b) => a.name.localeCompare(b.name)),
    [files]
  );
  const initialEnv =
    defaultEnvironmentId && environments.some((env) => env.id === defaultEnvironmentId)
      ? defaultEnvironmentId
      : environments[0]?.id ?? '';
  const [selectedEnv, setSelectedEnv] = useState(initialEnv);
  const [selectedVersion, setSelectedVersion] = useState('current');
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>(sortedFiles.map((f) => f.id));
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const toggleFile = (id: string) => {
    setSelectedFileIds((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const allSelected = sortedFiles.length > 0 && selectedFileIds.length === sortedFiles.length;

  const toggleSelectAll = () => {
    setSelectedFileIds(allSelected ? [] : sortedFiles.map((f) => f.id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEnv || selectedFileIds.length === 0) return;
    setDeploying(true);
    setError(null);
    try {
      const versionPath = resolveVersionPath(selectedVersion);
      await onDeploy(selectedEnv, selectedFileIds, versionPath, selectedVersion);
      setSuccess(`Deployment queued for ${selectedFileIds.length} file${selectedFileIds.length !== 1 ? 's' : ''}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger deployment');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="deploy-files-modal-title"
        className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-surface p-6 shadow-xl max-h-[80vh] overflow-y-auto"
      >
        <h2 id="deploy-files-modal-title" className="text-lg font-semibold text-content mb-1">
          Deploy Configuration Files
        </h2>
        <p className="text-xs text-content-tertiary mb-4">
          Select environment and files to deploy.
        </p>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
              <p className="text-sm font-medium text-green-700 dark:text-green-300">{success}</p>
            </div>
            <button type="button" onClick={onClose} className="w-full rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="deploy-files-env" className="block text-xs font-medium text-content-secondary mb-1">Environment *</label>
              <select
                id="deploy-files-env"
                required
                value={selectedEnv}
                onChange={(e) => setSelectedEnv(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
              >
                {environments.map((env) => (
                  <option key={env.id} value={env.id}>{env.name}</option>
                ))}
              </select>
            </div>

            <VersionSelector
              projectKey={projectKey}
              selectedVersion={selectedVersion}
              onVersionChange={setSelectedVersion}
            />

            <fieldset>
              <div className="mb-2 flex items-center justify-between">
                <legend className="block text-xs font-medium text-content-secondary">Files to deploy *</legend>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  disabled={sortedFiles.length === 0}
                  className="rounded px-1.5 py-0.5 text-xs font-medium text-brand-700 hover:bg-surface-raised disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-600 dark:text-brand-300"
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto rounded-md border border-border p-2">
                {sortedFiles.map((file) => (
                  <label key={file.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-raised cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFileIds.includes(file.id)}
                      onChange={() => toggleFile(file.id)}
                      className="rounded border-border text-brand-600 focus:ring-brand-600"
                    />
                    <span className="text-sm text-content">{file.name}</span>
                    {file.targetLocation && (
                      <span className="text-xs text-content-tertiary ml-auto truncate max-w-[180px]">{file.targetLocation}</span>
                    )}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-content-tertiary">
                {selectedFileIds.length} of {sortedFiles.length} selected
              </p>
            </fieldset>

            {error && (
              <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={deploying || !selectedEnv || selectedFileIds.length === 0}
                className="flex-1 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
              >
                {deploying ? 'Deploying...' : `Deploy ${selectedFileIds.length} file${selectedFileIds.length !== 1 ? 's' : ''}`}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={deploying}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---

interface DeployFilesTabProps {
  projectKey: string;
  environmentId?: string;
}

export function DeployFilesTab({ projectKey, environmentId }: DeployFilesTabProps) {
  const { data: configFiles = [], isLoading: filesLoading, error: filesError } = useConfigFiles(projectKey);
  const { data: environments = [], isLoading: envsLoading } = useEnvironments(projectKey);
  const { data: serviceDefinitions = [] } = useServiceDefinitions(projectKey);
  const { invalidateConfigFiles } = useInvalidateConfiguration(projectKey);
  const { data: configDeployStatus, isError: statusError } = useConfigDeployStatus(projectKey);

  const loading = filesLoading || envsLoading;
  const error = filesError ? 'Failed to load configuration files.' : null;

  const [showForm, setShowForm] = useState(false);
  const [editingFile, setEditingFile] = useState<ConfigurationFile | null>(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [overridePanelFileId, setOverridePanelFileId] = useState<string | null>(null);

  const sortedConfigFiles = useMemo(
    () => [...configFiles].sort((a, b) => a.name.localeCompare(b.name)),
    [configFiles]
  );

  const handleCreate = useCallback(async (data: Omit<ConfigurationFile, 'id' | 'projectKey'>) => {
    try {
      await createConfigFile(projectKey, data);
      await invalidateConfigFiles();
      setShowForm(false);
    } catch {
      // form stays open for retry
    }
  }, [projectKey, invalidateConfigFiles]);

  const handleUpdate = useCallback(async (data: Omit<ConfigurationFile, 'id' | 'projectKey'>) => {
    if (!editingFile) return;
    try {
      await updateConfigFile(projectKey, editingFile.id, data);
      await invalidateConfigFiles();
      setEditingFile(null);
    } catch {
      // form stays open for retry
    }
  }, [projectKey, editingFile, invalidateConfigFiles]);

  const handleDelete = useCallback(async (file: ConfigurationFile) => {
    if (!confirm(`Delete configuration file "${file.name}"? This cannot be undone.`)) return;
    try {
      await deleteConfigFile(projectKey, file.id);
      await invalidateConfigFiles();
    } catch {
      // silently fail
    }
  }, [projectKey, invalidateConfigFiles]);

  const handleDeploy = useCallback(async (environmentId: string, fileIds: string[], versionPath: string, version: string) => {
    await triggerConfigDeploy({
      projectKey,
      environment: environmentId,
      configFileIds: fileIds,
      versionPath,
      version,
    });
  }, [projectKey]);

  return (
    <div className="space-y-4">
      {/* Header with action buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-content">Configuration Files</h3>
        <div className="flex items-center gap-2">
          {sortedConfigFiles.length > 0 && environments.length > 0 && (
            <button
              type="button"
              onClick={() => setShowDeployModal(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 transition-colors"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Deploy Files
            </button>
          )}
          <button
            type="button"
            onClick={() => { setShowForm(true); setEditingFile(null); }}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add File
          </button>
        </div>
      </div>

      {/* Create form */}
      {showForm && !editingFile && (
        <ConfigFileForm
          serviceDefinitions={serviceDefinitions}
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Deploy Status by Environment */}
      {environments.length > 0 && (
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs font-semibold text-content-secondary mb-2">Deploy Status by Environment</p>
          <div className="flex flex-wrap gap-3">
            {environments.map((env) => (
              <div key={env.id} className="flex items-center gap-2">
                <span className="text-xs font-medium text-content">{env.name}:</span>
                <ConfigDeployStatusBadge
                  statusMap={configDeployStatus}
                  environmentId={env.id}
                  isError={statusError}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading / Error / List */}
      {loading && <LoadingState />}
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {!loading && !error && sortedConfigFiles.length === 0 && !showForm && (
        <EmptyState message="No configuration files defined yet. Add one to get started." />
      )}

      {!loading && sortedConfigFiles.length > 0 && (
        <ul className="space-y-2">
          {sortedConfigFiles.map((file) =>
            editingFile?.id === file.id ? (
              <li key={file.id}>
                <ConfigFileForm
                  serviceDefinitions={serviceDefinitions}
                  initial={file}
                  onSave={handleUpdate}
                  onCancel={() => setEditingFile(null)}
                />
              </li>
            ) : (
              <li key={file.id}>
                <ConfigFileListItem
                  projectKey={projectKey}
                  configFile={file}
                  environments={environments}
                  onEdit={(f) => { setEditingFile(f); setShowForm(false); }}
                  onDelete={handleDelete}
                  onManageOverrides={(f) => setOverridePanelFileId(overridePanelFileId === f.id ? null : f.id)}
                />
                {overridePanelFileId === file.id && (
                  <div className="mt-2 ml-4">
                    <EnvironmentOverridePanel
                      projectKey={projectKey}
                      configFile={file}
                      environments={environments}
                    />
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      )}

      {environments.length === 0 && !loading && (
        <p className="text-xs text-content-tertiary italic">
          No environments configured. Create environments in Admin before deploying.
        </p>
      )}

      {/* Deploy Files Modal */}
      {showDeployModal && (
        <DeployFilesModal
          files={sortedConfigFiles}
          environments={environments}
          projectKey={projectKey}
          defaultEnvironmentId={environmentId}
          onClose={() => setShowDeployModal(false)}
          onDeploy={handleDeploy}
        />
      )}
    </div>
  );
}
